import ROUTES from '@/services/routes';
import { upperCase } from 'lodash';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, type LinkProps, useLocation } from 'react-router-dom';
import LogoSmall from '@/components/logo-small';

interface NavigationItem {
  title: string;
  path: string;
  dropdown?: NavigationItem[];
}

const ActiveLink: React.FC<LinkProps & { dropdown?: boolean }> = ({
  to,
  children,
  dropdown,
  ...props
}) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      className={`px-5 py-3 text-base text-slate-50 flex items-center hover:text-slate-200 ${
        isActive && 'font-semibold underline hover:text-slate-200'
      } ${dropdown ? 'pr-1' : ''}`}
      to={to}
      {...props}
    >
      {children}
      {dropdown && (
        <svg
          role="img"
          aria-label="open menu"
          className="w-4 h-4 ml-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      )}
    </Link>
  );
};

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const navigation: NavigationItem[] = [
    { title: 'Home', path: ROUTES.HOME },
    { title: 'Over', path: ROUTES.ABOUT },
    {
      title: 'Therapie',
      path: ROUTES.THERAPY_FULL,
      dropdown: [
        { title: 'Individueel', path: `${ROUTES.THERAPY_FULL}/#individueel` },
        { title: 'Relatie', path: `${ROUTES.THERAPY_FULL}/#relatie` },
        { title: 'Systeem', path: `${ROUTES.THERAPY_FULL}/#systeem` },
        { title: 'Tarieven', path: `${ROUTES.THERAPY_FULL}/#tarieven` }
      ]
    },
    {
      title: 'Supervisie',
      path: ROUTES.SUPERVISION
    },
    { title: 'Contact', path: ROUTES.CONTACT_FULL }
  ];

  const handleDropdownToggle = (title: string) => {
    setActiveDropdown(activeDropdown === title ? null : title);
  };

  return (
    <nav className="z-50 w-full bg-header border-b border-header-border py-3 fixed top-0 left-0 right-0 h-16">
      <div className="container mx-auto flex justify-between items-center h-full px-4 md:px-8 lg:px-12 xl:px-16">
        <div className="flex w-full md:flex-none md:w-fit justify-between md:shrink-0">
          <Link to={ROUTES.HOME} className="font-cookie text-4xl text-slate-50">
            <div className="w-32 sm:w-36 md:w-44 text-white">
              <LogoSmall className="transition-all duration-300 h-auto w-full" />
              {/*<LogoParallex />*/}
            </div>
          </Link>
          <button
            type="button"
            className="text-slate-800 hover:text-slate-600 outline-hidden md:hidden my-2 p-2 -mr-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation menu"
            aria-expanded={open}
          >
            {open ? (
              <svg
                role="img"
                aria-label="close"
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                role="img"
                aria-label="open"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
        <div className="grow text-center">
          <ul
            className={`space-x-8 justify-center md:font-medium md:flex md:mt-0 ${
              open
                ? 'absolute inset-x-0 top-16 px-4 py-6 border-b border-slate-700 md:border-none md:static bg-header shadow-lg md:shadow-none z-50'
                : 'hidden'
            }`}
          >
            <div className="items-center space-y-1 md:flex md:space-x-6 md:space-y-0 divide-y divide-slate-600 md:divide-y-0">
              {navigation.map((item) => (
                <li key={item.title} className="relative group pt-3 first:pt-0 md:pt-0">
                  <div className="flex items-center justify-between md:justify-center">
                    <ActiveLink to={item.path} onClick={() => setOpen(false)} dropdown={false}>
                      {upperCase(item.title)}
                    </ActiveLink>
                    {item.dropdown && (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                      <svg
                        role="img"
                        aria-label="toggle dropdown"
                        onClick={() => handleDropdownToggle(item.title)}
                        className={`w-4 h-4 transition-transform duration-200 cursor-pointer text-slate-50 md:hidden ${
                          activeDropdown === item.title ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    )}
                  </div>
                  {item.dropdown && (
                    <ul
                      className={`${
                        activeDropdown === item.title ? 'block' : 'hidden'
                      } pl-4 mt-2 space-y-1 border-l-2 border-slate-600 md:absolute md:left-0 md:-mt-2 md:pt-3 md:w-48 md:bg-header md:rounded-md md:shadow-lg md:pb-1 md:z-10 md:opacity-0 md:invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out md:group-hover:block md:pl-0 md:border-none`}
                    >
                      {item.dropdown.map((dropdownItem) => (
                        <li key={`${item.title}-${dropdownItem.title}`}>
                          <ActiveLink
                            to={dropdownItem.path}
                            onClick={() => {
                              setOpen(false);
                              setActiveDropdown(null);
                            }}
                          >
                            {upperCase(dropdownItem.title)}
                          </ActiveLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </div>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
